import { useState } from 'react';
import {
  Card,
  Group,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
  Checkbox,
  Button,
} from '@cowbell/ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleExclamation,
  faPaperPlane,
} from '@fortawesome/pro-regular-svg-icons';
import snakeCase from 'lodash/snakeCase';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

import {
  AffectedProductStatus,
  SpotlightStatus,
  RowState,
  SpotlightProduct,
} from './spotlightDetails.types';
import { usePersona } from '../../../components/hooks/usePersona';
import CurrentVersionColumn from './CurrentVersionColumn';
import ActionColumn from './ActionColumn';
import { DeclineProductModal } from './DeclineProductModal';
import classes from './SpotlightAffectedProductsTable.module.css';
import { useActor } from '../../../components/hooks/useActor';
import { getSpotlightUserAccessMode } from '../getSpotlightUserAccessMode';
import { FEATURE_FLAG_KEYS } from '../../../config/growthbook';
import { resolveEffectiveStatus } from './resolveEffectiveStatus';

interface DeclineModalContext {
  productName: string;
  customerVersion: string;
}

interface SpotlightAffectedProductsTableProps {
  products: SpotlightProduct[];
  rowStates: Record<string, RowState>;
  onVersionChange: (productName: string, value: string) => void;
  onPatch: (productName: string, customerVersion: string) => void;
  onNotApplicable: (productName: string) => void;
  onUndo: (productName: string) => void;
  onSubmitForReview: (productName: string, customerVersion: string) => void;
  handleContactCyberExpert: (productName: string) => void;
  onDecline: (
    productName: string,
    customerVersion: string,
    declinedReason: string
  ) => Promise<boolean>;
  spotlightStatus: SpotlightStatus;
  onSendEmailToDeclineProducts: (productNames: string[]) => void;
}

export function SpotlightAffectedProductsTable(
  props: SpotlightAffectedProductsTableProps
) {
  const persona = usePersona();
  const actor = useActor();
  const isSpotlightV2PgSource = useFeatureIsOn(
    FEATURE_FLAG_KEYS.SPOTLIGHT_V2_PG_SOURCE
  );
  const userAccessMode = getSpotlightUserAccessMode(
    persona,
    actor?.roleName,
    isSpotlightV2PgSource
  );
  const [declineModalContext, setDeclineModalContext] =
    useState<DeclineModalContext | null>(null);
  const [declineSubmitting, setDeclineSubmitting] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const {
    products,
    rowStates,
    onVersionChange,
    onPatch,
    onNotApplicable,
    onUndo,
    onSubmitForReview,
    handleContactCyberExpert,
    onDecline,
    spotlightStatus,
    onSendEmailToDeclineProducts,
  } = props;

  const handleRequestDecline = (
    productName: string,
    customerVersion: string
  ) => {
    setDeclineModalContext({ productName, customerVersion });
  };

  const handleDeclineModalClose = () => {
    if (!declineSubmitting) {
      setDeclineModalContext(null);
    }
  };

  const handleDeclineConfirm = async (declinedReason: string) => {
    if (!declineModalContext) {
      return;
    }

    setDeclineSubmitting(true);

    try {
      const success = await onDecline(
        declineModalContext.productName,
        declineModalContext.customerVersion,
        declinedReason
      );

      if (success) {
        setDeclineModalContext(null);
      }
    } catch (error) {
      console.error('Error declining product', error);
    } finally {
      setDeclineSubmitting(false);
    }
  };

  const handleSendEmailToDeclineProducts = () => {
    onSendEmailToDeclineProducts(selectedRows);
  };

  return (
    <>
      <Card mt="md" padding="lg" shadow="sm" radius="md">
        <Title order={3} mb="xs">
          {persona.isCowbell
            ? 'Verify the Spotlight details'
            : 'Verify Installed Product Versions'}
        </Title>
        <Text c="dimmed" size="md" mb="md" maw="60%">
          {persona.isCowbell
            ? `Enter the version currently installed in the policyholder's environment and validate it against the required patched threshold. If it meets the threshold, mark the product as Patched. If it is below the threshold, decline the submission so the policyholder can review and resubmit. Mark products as Not Applicable if they are not in use.`
            : `Verify the version currently installed in your environment and enter it below. The version will be checked against the required patched threshold. If it meets or exceeds the required threshold, the product will be marked as Patched. If it is below the required threshold, then the product will be declined and you'll be prompted to update the version as per the required range shown. Select Not Applicable for products that are not used in your environment.`}
        </Text>
        {persona.isCowbell && selectedRows.length > 0 && (
          <Group justify="flex-end" mb="md">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSendEmailToDeclineProducts}
            >
              <Group gap="xs">
                <FontAwesomeIcon icon={faPaperPlane} />
                <Text size="md" fw={500}>
                  Send Decline Email
                </Text>
              </Group>
            </Button>
          </Group>
        )}
        <Table
          className={classes.table}
          striped
          highlightOnHover
          withTableBorder
          horizontalSpacing="md"
          verticalSpacing="md"
        >
          <colgroup>
            {persona.isCowbell && <col className={classes.colCheckbox} />}
            <col className={classes.colProduct} />
            <col className={classes.colVersion} />
            <col className={classes.colVersion} />
            <col className={classes.colAction} />
          </colgroup>
          <Table.Thead bg="var(--mantine-color-gray-1)">
            <Table.Tr>
              {persona.isCowbell && (
                <Table.Th className={`${classes.cellTop} ${classes.wrapCell}`}>
                  <Checkbox
                    aria-label="Select row"
                    checked={
                      selectedRows.length ===
                      products.filter(
                        (product) =>
                          product.status === AffectedProductStatus.DECLINED
                      ).length
                    }
                    disabled={products.every(
                      (product) =>
                        product.status !== AffectedProductStatus.DECLINED
                    )}
                    onChange={(event) =>
                      setSelectedRows(
                        event.currentTarget.checked
                          ? products
                              .filter(
                                (product) =>
                                  product.status ===
                                  AffectedProductStatus.DECLINED
                              )
                              .map((product) => product.name)
                          : []
                      )
                    }
                  />
                </Table.Th>
              )}
              <Table.Th className={`${classes.cellTop} ${classes.wrapCell}`}>
                Product
              </Table.Th>
              <Table.Th className={`${classes.cellTop} ${classes.wrapCell}`}>
                Affected Version
              </Table.Th>
              <Table.Th className={`${classes.cellTop} ${classes.wrapCell}`}>
                Your Current Version
              </Table.Th>
              <Table.Th className={classes.cellTop}>Action</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {Array.isArray(products) && products.length > 0 ? (
              products.map((product) => {
                const rowKey = snakeCase(product.name);
                const rowState = rowStates[rowKey] ?? {
                  customerVersion: product.customerVersion ?? '',
                };

                let status = (product.status ??
                  AffectedProductStatus.UNPATCHED) as AffectedProductStatus;
                let declinedReason = product.declinedReason;

                if (isSpotlightV2PgSource) {
                  status = resolveEffectiveStatus(
                    rowState.status,
                    product.status
                  );
                  declinedReason = product.statusReason;
                }

                return (
                  <Table.Tr
                    key={product.name}
                    bg={
                      selectedRows.includes(product.name)
                        ? 'var(--mantine-color-blue-light)'
                        : undefined
                    }
                  >
                    {persona.isCowbell && (
                      <Table.Td
                        className={`${classes.cellTop} ${classes.wrapCell}`}
                        colSpan={1}
                      >
                        <Checkbox
                          aria-label="Select row"
                          checked={selectedRows.includes(product.name)}
                          disabled={status !== AffectedProductStatus.DECLINED}
                          onChange={(event) =>
                            setSelectedRows(
                              event.currentTarget.checked
                                ? [...selectedRows, product.name]
                                : selectedRows.filter(
                                    (name) => name !== product.name
                                  )
                            )
                          }
                        />
                      </Table.Td>
                    )}
                    <Table.Td
                      className={`${classes.cellTop} ${classes.wrapCell}`}
                    >
                      <Stack gap="xs">
                        <Group gap="xs" align="center" wrap="nowrap">
                          {status === AffectedProductStatus.DECLINED &&
                          declinedReason ? (
                            <Tooltip label={declinedReason} color="red">
                              <FontAwesomeIcon
                                className={classes.productIcon}
                                icon={faCircleExclamation}
                                color="var(--mantine-color-red-6)"
                              />
                            </Tooltip>
                          ) : (
                            <FontAwesomeIcon
                              className={classes.productIcon}
                              icon={faCircleExclamation}
                              color="var(--custom-text-warning-default)"
                            />
                          )}
                          <Text size="md" className={classes.wrappingText}>
                            {product.name}
                          </Text>
                        </Group>
                      </Stack>
                    </Table.Td>
                    <Table.Td
                      className={`${classes.cellTop} ${classes.wrapCell}`}
                    >
                      <Text size="md" className={classes.cellTextWrap}>
                        {product.affectedVersion || '--'}
                      </Text>
                    </Table.Td>
                    <Table.Td className={classes.cellTop}>
                      <CurrentVersionColumn
                        persona={persona}
                        status={status}
                        product={product}
                        rowState={rowState}
                        onVersionChange={onVersionChange}
                        userAccessMode={userAccessMode}
                      />
                    </Table.Td>
                    <Table.Td
                      className={`${classes.cellTop} ${classes.wrapCell}`}
                    >
                      <ActionColumn
                        persona={persona}
                        status={status}
                        product={product}
                        onUndo={onUndo}
                        onPatch={onPatch}
                        rowState={rowState}
                        onSubmitForReview={onSubmitForReview}
                        onNotApplicable={onNotApplicable}
                        handleContactCyberExpert={handleContactCyberExpert}
                        onRequestDecline={handleRequestDecline}
                        spotlightStatus={spotlightStatus}
                        userAccessMode={userAccessMode}
                      />
                    </Table.Td>
                  </Table.Tr>
                );
              })
            ) : (
              <Table.Tr>
                <Table.Td colSpan={persona.isCowbell ? 5 : 4}>
                  <Text size="md" c="dimmed" ta="center">
                    No affected products found.
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Card>
      <DeclineProductModal
        opened={declineModalContext !== null}
        productName={declineModalContext?.productName ?? ''}
        onClose={handleDeclineModalClose}
        onConfirm={handleDeclineConfirm}
        isSubmitting={declineSubmitting}
      />
    </>
  );
}
